import React, { useState, useEffect } from "react";
import { Select } from "@/components/select";
import { Spinner } from "../../../components/spinner";
import { useUpdateQuestion } from "@/apis/questions/updateQuestion";
import { useQueryGetCategories } from "@/apis/categories/getCategories";
import type { ICategory } from "@/apis/categories/types";
import { message } from "antd";
import type { IQuestion } from "@/apis/exam_papers/types";

interface CategorySelectorProps {
  question: IQuestion;
  examType: string;
  subject: string;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  question,
  examType,
  subject,
}) => {
  // 선택된 카테고리 상태
  const [selectedCategory, setSelectedCategory] = useState(
    question?.category_id || ""
  );
  // 업데이트 상태
  const [isUpdating, setIsUpdating] = useState(false);

  // 카테고리 목록 조회
  const {
    data: categoriesData,
    isLoading,
    error: categoriesError,
  } = useQueryGetCategories({
    exam_type: examType,
    subject: subject,
  });

  // 카테고리 업데이트 뮤테이션
  const updateQuestionMutation = useUpdateQuestion();

  // 현재 카테고리가 변경되면 선택된 카테고리도 업데이트
  useEffect(() => {
    setSelectedCategory(question?.category_id || "");
  }, [question]);

  // 카테고리 변경 처리
  const handleCategoryChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const newCategory = e.target.value;
    setSelectedCategory(newCategory);

    // 실제 변경이 있는 경우에만 API 호출
    if (newCategory !== question?.category_id) {
      setIsUpdating(true);
      try {
        await updateQuestionMutation.mutateAsync({
          id: question?.id,
          data: { category_id: newCategory },
        });
        message.success("Category updated successfully");
      } catch (error) {
        console.error("Failed to update category:", error);
        // 실패 시 원래 값으로 복원
        setSelectedCategory(question?.category_id || "");
        message.error("Failed to update category");
      } finally {
        setIsUpdating(false);
      }
    }
  };

  // 사용할 수 있는 카테고리 목록
  const categories = categoriesData || [];

  return (
    <div className="relative inline-block w-full">
      <Select
        value={selectedCategory}
        onChange={handleCategoryChange}
        disabled={isLoading || isUpdating}
        className={`text-sm w-full pr-8 ${isUpdating ? "opacity-70" : ""}`}
      >
        {isLoading ? (
          <option>Loading categories...</option>
        ) : categoriesError ? (
          <option value="">Error loading categories</option>
        ) : (
          <>
            <option value="">Select category</option>
            {Array.isArray(categories) && categories.length > 0 ? (
              categories.map((category: ICategory) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))
            ) : (
              <option value="">No categories available</option>
            )}
          </>
        )}
      </Select>

      {/* Status indicators */}
      {isUpdating && (
        <div className="absolute right-2 top-1/2 -translate-y-1/2">
          <Spinner className="w-4 h-4" />
        </div>
      )}
    </div>
  );
};

export default CategorySelector;
